import classNames from 'classnames';
import { useAppDispatch } from 'hooks/redux';
import _ from 'lodash';
import React, { Fragment, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';

import ChevronIcon from '@mui/icons-material/ChevronRightRounded';
import CheckIcon from '@mui/icons-material/DoneRounded';
import ErrorIcon from '@mui/icons-material/Error';
import LinkIcon from '@mui/icons-material/LinkRounded';
import LockIcon from '@mui/icons-material/LockRounded';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { scheduledTransactionsActions, scheduledTransactionsTypes } from 'app-components/flux/scheduled-transactions';
import { getLogger } from 'app-components/utils/core';

import { billPresentmentAPI } from 'app-components/flux/bill-presentment';
import { useBillersWithLogos } from 'app-components/flux/bill-presentment/billPresentmentHooks';
import type {
  Biller,
  EnrollResponse,
  Field,
  Parameter,
} from 'app-components/flux/bill-presentment/billPresentmentTypes';

import ConnectFIs from 'assets/illustrations/ConnectFIs.svg';
import Dump from 'components/Dump';

import { StepEnum } from '../types';

const log = getLogger('BillPresentment/LoginPage');

const useStyles = makeStyles()((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    maxHeight: 431,
    overflowY: 'auto',
    flexGrow: 1,
    boxSizing: 'border-box',
    width: '100%',
  },

  scrollContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    padding: '12px',
    overflowY: 'auto',
    overflowX: 'hidden',
    boxSizing: 'border-box',
  },

  scrollInner: {
    width: '100%',
    maxWidth: 464,
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
    textAlign: 'left',
  },

  form: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    paddingBottom: 24,
  },

  formActions: {
    height: 72,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0 24px',
    borderTop: `1px solid ${theme.palette.border.light}`,
    backgroundColor: theme.palette.background.paper,
    width: '100%',
    margin: '0 auto',
    boxSizing: 'border-box',
    flexShrink: 0,
    paddingTop: 12,
  },

  footer: {
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.text.secondary,
  },

  subLock: {
    height: 20,
    width: 20,
    marginRight: 8,
    color: theme.palette.icon.light,
  },

  loadingFade: {
    position: 'absolute',
    top: 94,
    left: 0,
    width: '100%',
    height: 'calc(100% - 154px)',
    backgroundColor: theme.applyOpacityToHex?.(theme.palette.background.paper, 1) ?? theme.palette.background.paper,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 2,
  },

  row: {
    display: 'flex',
    justifyContent: 'center',
    paddingTop: 40,
    alignContent: 'center',
    color: theme.palette.text.secondary,
  },

  check: {
    color: theme.palette.primary.main,
    paddingRight: 10,
    paddingBottom: 5,
    transform: 'scale(1.5)',
  },

  brandingIcon: {
    position: 'absolute',
    left: -72,
    top: 0,
    width: 64,
    height: 64,
    borderRadius: 8,
    objectFit: 'contain',
    border: `1px solid ${theme.palette.border.light}`,
    backgroundColor: theme.palette.background.paper,
  },
  header: {
    // width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    height: 80,
    flexShrink: 0,
    position: 'relative',
  },

  link: {
    textDecoration: 'none',
    color: theme.palette.text.secondary,
  },

  termButton: {
    padding: '8px 16px 8px 0',
    borderRadius: 8,
  },

  rotate: {
    transform: 'rotate(90deg)',
  },

  terms: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    marginLeft: 28,
  },

  infoIcon: {
    marginLeft: 2,
    marginRight: 2,
    height: 18,
  },

  tooltipText: {
    color: theme.palette.error.main,
    display: 'flex',
    alignItems: 'center',
  },

  tooltip: {
    backgroundColor: theme.palette.error.light,
  },

  tooltipArrow: {
    color: theme.palette.error.light,
  },

  tooltipStyle: {
    padding: 12,
    borderRadius: 8,
  },

  popper: {
    marginBottom: '8px !important',
  },
}));

function makeRegex(regexString: string) {
  let pattern = regexString;
  let flags = '';

  // if it’s wrapped in slashes, pull pattern & flags out
  const slashMatch = regexString.match(/^\/(.+)\/([a-z]*)$/i);
  if (slashMatch) {
    pattern = slashMatch[1];
    flags = slashMatch[2];
  }
  return new RegExp(pattern, flags);
}

interface LoginProps {
  enrollment?: EnrollResponse | null;
  biller?: Biller | null;
  setSteps: (data: any) => void;
  GUID: string;
  series: scheduledTransactionsTypes.ScheduledTransactionType;
}

const SubmittingOverlay = () => {
  const { classes } = useStyles();
  return (
    <div className={classes.loadingFade}>
      <CircularProgress size={64} />
      <div className={classes.row}>
        <CheckIcon className={classes.check} />
        <Typography variant='paragraphRegular'>Connecting to Biller</Typography>
      </div>
    </div>
  );
};

interface FormField extends Field {
  value: string;
}
interface FormParam extends Parameter {
  fields: FormField[];
}

function initializeForm(enrollment?: EnrollResponse | null) {
  if (!enrollment) return [];
  return enrollment.parameters.map((param: Parameter) => ({
    ...param,
    fields: param.fields.map((field) => ({ ...field, value: '' })),
  }));
}

function getFieldError(field: FormField) {
  const { validation, value } = field;
  if (validation) {
    if (validation.required && !field.value) {
      return 'Field is required';
    }
    if ((validation.required || field.value) && validation.minLength && value.length < validation.minLength) {
      return `Must be at least ${validation.minLength} characters`;
    }
    if (validation.maxLength && value.length > validation.maxLength) {
      return `Must be less than ${validation.maxLength + 1} characters`;
    }
    if (validation.regex) {
      const reg = makeRegex(validation.regex);
      if (reg && !reg.exec(value)) {
        return 'Check format and try again';
      }
    }
  }
  return null;
}

function hasFormError(formValues: FormParam[]) {
  return formValues.some((param) => param.fields.some(getFieldError));
}

const LoginView = ({ enrollment, biller, setSteps, GUID, series }: LoginProps) => {
  const { classes } = useStyles();
  const { billersWithLogos } = useBillersWithLogos(biller ? [biller] : []);
  const dispatch = useAppDispatch();
  const fieldRefs = React.useRef<Record<string, HTMLDivElement | null>>({});

  const [formValues, setFormValues] = useState<FormParam[]>(() => initializeForm(enrollment));
  const [validate, setValidate] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);

  const [showTerms, setShowTerms] = useState<boolean>();

  useEffect(() => {
    // there can be a delay in enrollment info in the reconnect flow (since we skip search)
    //  so we need to re-init the form when we get enrollment data from the server in that case
    if (enrollment) {
      setFormValues(initializeForm(enrollment));
    }
  }, [enrollment]);

  const handleFormSubmission = async () => {
    const hasError = hasFormError(formValues);
    setValidate(true);
    log.info('LoginView: handleFormSubmission', { formValues });
    if (hasError) {
      log.debug('Form has errors, scrolling to first error field');
      for (const param of formValues) {
        for (const field of param.fields) {
          const error = getFieldError(field);
          log.debug('Checking field for error:', field.name, 'Error:', error);
          if (error) {
            const ref = fieldRefs.current[field.name];
            if (ref && typeof ref.scrollIntoView === 'function') {
              log.debug('Scrolling to field with error:', field.name);
              // Scroll the field into view
              ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
              return;
            }
          }
        }
      }
    } else if (biller) {
      // valid payload, do enroll
      setSubmitting(true);
      const parameters = formValues.map((param) => ({
        name: param.name,
        fields: param.fields.map((field) => ({
          name: field.name,
          value: field.value,
        })),
      }));

      const dataToSend = {
        nickName: biller.name,
        reference: GUID,
        billerId: biller.id,
        data: {
          consent: {
            value: 'Yes',
          },
          printSuppression: {
            value: enrollment?.printSuppression?.type === 'Mandatory' ? 'Yes' : 'No',
          },
          parameters,
        },
      };

      const response = await billPresentmentAPI.submit(dataToSend);
      log.debug('Submit response', response);
      if (response.status === 'SUBMITTED' && response.id) {
        // here
        log.debug('Submit successful!', response);
        const confirmResponse = await billPresentmentAPI.confirm(response.id);
        log.debug('Confirm response', confirmResponse);
        if (confirmResponse.status === 'CONNECTED' || confirmResponse.status === 'PENDING') {
          log.debug('Confirm successful!', response);
          dispatch(
            scheduledTransactionsActions.updateScheduledTransaction(
              scheduledTransactionsTypes.mkScheduledTransaction({
                id: series?.id,
                presentmentAccountId: confirmResponse.id,
              }),
            ),
          );
        }
        // TODO: look at response status to decide whether to send them to the complete page or not
        setSteps(StepEnum.PENDING);
      }
    }
    setSubmitting(false);
  };

  const websiteLink = biller?.url;

  const billerLogo = billersWithLogos[0]?.logoKey;

  return (
    <>
      <div className={classes.root}>
        <Dump obj={biller} />

        <div className={classes.scrollContainer}>
          <div className={classes.scrollInner}>
            <div className={classes.header}>
              <img alt='Bank Icon' className={classes.brandingIcon} src={billerLogo || ConnectFIs} />
              <Typography variant='titleMedium'>{biller?.name}</Typography>

              <Typography variant='paragraphRegular' noWrap>
                {websiteLink && (
                  <Link to={websiteLink} target='_blank' className={classes.link}>
                    {websiteLink}
                  </Link>
                )}
                {biller?.classification && ` | ${biller.classification}`}
              </Typography>
            </div>

            <Typography variant='bodyRegular' margin='12px 0'>
              Enter the following account details to connect.
            </Typography>

            <div className={classes.form}>
              <Dump obj={enrollment} />

              {submitting ? (
                <SubmittingOverlay />
              ) : (
                <>
                  {formValues.map((parameter, pIndex) => {
                    const isUSAddress = parameter.name === 'us-address';

                    if (!parameter?.fields) return null;
                    return (
                      <Fragment key={parameter.name}>
                        {parameter.fields.length > 1 ? (
                          <Typography variant='captionMedium' marginTop={2}>
                            {parameter.label}
                          </Typography>
                        ) : null}

                        {isUSAddress ? (
                          <Box display='flex' width='100%' flexDirection='column' gap={1.5} mt={1}>
                            {/* Number + Street in a row */}
                            <Box display='flex' gap={2} width='100%'>
                              {parameter.fields
                                .reduce<FormField[]>((acc, field) => {
                                  if (['number', 'street'].includes(field.name)) {
                                    acc.push(field);
                                  }
                                  return acc;
                                }, [])
                                .map((field, fIndex) => {
                                  const fieldError = validate && getFieldError(field);
                                  const flex = field.name === 'number' ? 1 : 2;

                                  return (
                                    <Box
                                      key={field.name}
                                      ref={(el: HTMLDivElement | null) => {
                                        fieldRefs.current[field.name] = el;
                                      }}
                                      flex={flex}
                                    >
                                      <TextField
                                        value={field.value}
                                        label={field.label}
                                        onChange={(e) => {
                                          setFormValues((oldForm) =>
                                            _.set([...oldForm], [pIndex, 'fields', fIndex, 'value'], e?.target?.value),
                                          );
                                        }}
                                        fullWidth
                                        margin='dense'
                                        helperText={fieldError ?? field.helpText}
                                        error={Boolean(fieldError)}
                                      />
                                    </Box>
                                  );
                                })}
                            </Box>
                            {/* Render all other fields normally, stacked */}
                            {parameter.fields
                              .reduce<FormField[]>((acc, field) => {
                                if (field.name !== 'number' && field.name !== 'street') {
                                  acc.push(field);
                                }
                                return acc;
                              }, [])
                              .map((field, fIndex) => {
                                const fieldError = validate && getFieldError(field);
                                return (
                                  <TextField
                                    key={field.name}
                                    value={field.value}
                                    label={field.label}
                                    onChange={(e) => {
                                      setFormValues((oldForm) =>
                                        _.set([...oldForm], [pIndex, 'fields', fIndex, 'value'], e.target.value),
                                      );
                                    }}
                                    fullWidth
                                    margin='dense'
                                    helperText={fieldError ?? field.helpText}
                                    error={Boolean(fieldError)}
                                  />
                                );
                              })}
                          </Box>
                        ) : (
                          parameter.fields.map((field, fIndex) => {
                            const fieldError = validate && getFieldError(field);
                            return (
                              <TextField
                                key={field.name}
                                value={field.value}
                                label={field.label}
                                onChange={(e) => {
                                  setFormValues((oldForm) =>
                                    _.set([...oldForm], [pIndex, 'fields', fIndex, 'value'], e?.target?.value),
                                  );
                                }}
                                fullWidth
                                margin='dense'
                                helperText={fieldError ?? field.helpText}
                                error={Boolean(fieldError)}
                              />
                            );
                          })
                        )}
                      </Fragment>
                    );
                  })}

                  <Divider flexItem sx={{ marginY: 1 }} />
                  <ButtonBase id='terms-button' onClick={() => setShowTerms(!showTerms)} className={classes.termButton}>
                    <Typography variant='paragraphRegular' color='textSecondary'>
                      <ChevronIcon className={showTerms ? classes.rotate : ''} sx={{ marginRight: '4px' }} />
                      {`By connecting, you agree to your biller’s terms.`}
                    </Typography>
                  </ButtonBase>

                  {showTerms ? (
                    <div className={classes.terms}>
                      <Typography variant='paragraphMedium' color='textSecondary' marginBottom='2px'>
                        From {biller?.name}:
                      </Typography>
                      <Typography variant='captionRegular' color='textSecondary'>
                        {`You consent to ${biller?.name} sharing your billing information with this service in line with biller’s `}
                        {'terms'}
                      </Typography>
                      <></>
                    </div>
                  ) : null}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className={classes.formActions}>
        <Typography variant='paragraphRegular' className={classes.footer} component='div'>
          <LockIcon className={classes.subLock} />
          Secure connection, encrypted end-to-end
        </Typography>

        <Tooltip
          arrow
          classes={{
            tooltip: classNames(classes.tooltip, classes.tooltipStyle),
            arrow: classes.tooltipArrow,
            tooltipPlacementTop: classes.popper,
          }}
          title={
            <div className={classes.tooltipText}>
              <ErrorIcon className={classes.infoIcon} />
              <Typography variant='paragraphRegular'> Fields need attention</Typography>
            </div>
          }
          placement='top'
          open={validate && hasFormError(formValues)}
        >
          <span>
            <Button
              id='submitButton'
              type='submit'
              variant='contained'
              startIcon={<LinkIcon />}
              onClick={handleFormSubmission}
            >
              Connect
            </Button>
          </span>
        </Tooltip>
      </div>
    </>
  );
};

export default LoginView;
